import React from 'react';

import { cn } from '../lib/utils';
import { DialogFooter } from './ui/dialog';

interface DialogActionsProps {
  /**
   * Cancel (or Close), then the primary on the far right — nothing else.
   * There is deliberately no leading or destructive slot: Delete, Decommission
   * and every other record-destroying action sits in the dialog body, below
   * the fields (DESIGN_SYSTEM.md §5 dialog anatomy).
   */
  children: React.ReactNode;
  /**
   * A short muted line on the left describing the pending change ("2 roles
   * change"). Text only — never a control.
   */
  status?: string | null;
}

/**
 * The footer of a create/edit dialog. Render it as a sibling AFTER the
 * DialogBody (or inside a DialogForm, after the body), never inside the scroll
 * pane, so the primary action never scrolls out of view on a tall form.
 * Non-submit buttons inside a form must be type="button".
 */
export const DialogActions: React.FC<DialogActionsProps> = ({ children, status }) => (
  <DialogFooter className="shrink-0 gap-2 sm:items-center sm:space-x-0">
    {status !== undefined ? (
      <p className="text-xs text-muted-foreground sm:mr-auto" aria-live="polite">
        {status}
      </p>
    ) : null}
    {children}
  </DialogFooter>
);

/**
 * A dialog form. DialogContent is a flex column, so the form has to be one
 * too, or DialogBody inside it loses its height and stops scrolling. Wrap the
 * DialogBody and DialogActions — the header stays outside.
 */
export const DialogForm = React.forwardRef<HTMLFormElement, React.FormHTMLAttributes<HTMLFormElement>>(
  // noValidate by default: validation is the dialog's own, every rule at once
  // under its own field (DESIGN_SYSTEM §5 Validation). Without it a native
  // constraint on a control — `min`/`max` on a number input — makes the browser
  // refuse the submit with its own bubble, and the page's messages never show.
  ({ className, noValidate = true, ...props }, ref) => (
    <form
      ref={ref}
      noValidate={noValidate}
      className={cn('flex min-h-0 flex-1 flex-col gap-4', className)}
      {...props}
    />
  ),
);
DialogForm.displayName = 'DialogForm';
